// (c) chaehongee. Confidential and proprietary.

import type { Point, Rect } from '../types'

export type { Point, Rect }

export function distanceSq(ax: number, ay: number, bx: number, by: number): number {
    const dx = ax - bx
    const dy = ay - by
    return dx * dx + dy * dy
}

// Douglas-Peucker polyline simplification for [x1,y1,x2,y2,...]
export function simplifyPolyline(points: number[], tolerance: number): number[] {
    if (points.length <= 4) return points
    const sqTol = tolerance * tolerance
    const pts: Array<Point> = []
    for (let i = 0; i < points.length; i += 2) pts.push({ x: points[i], y: points[i + 1] })

    function getSqSegDist(p: Point, a: Point, b: Point): number {
        let x = a.x
        let y = a.y
        let dx = b.x - x
        let dy = b.y - y
        if (dx !== 0 || dy !== 0) {
            const t = ((p.x - x) * dx + (p.y - y) * dy) / (dx * dx + dy * dy)
            if (t > 1) { x = b.x; y = b.y }
            else if (t > 0) { x += dx * t; y += dy * t }
        }
        dx = p.x - x
        dy = p.y - y
        return dx * dx + dy * dy
    }

    function simplifyDP(start: number, end: number, out: Point[]): void {
        let maxDist = 0
        let index = -1
        for (let i = start + 1; i < end; i++) {
            const dist = getSqSegDist(pts[i], pts[start], pts[end])
            if (dist > maxDist) { index = i; maxDist = dist }
        }
        if (maxDist > sqTol && index !== -1) {
            simplifyDP(start, index, out)
            out.push(pts[index])
            simplifyDP(index, end, out)
        }
    }

    const out: Point[] = [pts[0]]
    simplifyDP(0, pts.length - 1, out)
    out.push(pts[pts.length - 1])
    const flat: number[] = []
    for (const p of out) { flat.push(p.x, p.y) }
    return flat
}

export function pointInsideRect(rect: Rect | null, x: number, y: number): boolean {
    if (!rect) return false
    return x >= rect.x && y >= rect.y && x <= rect.x + rect.w && y <= rect.y + rect.h
}

export function rectsIntersect(a: Rect, b: Rect): boolean {
    return !(a.x + a.w < b.x || a.x > b.x + b.w || a.y + a.h < b.y || a.y > b.y + b.h)
}

export function clampPointToRect(rect: Rect | null, x: number, y: number): Point | null {
    if (!rect) return null
    const cx = Math.min(Math.max(x, rect.x), rect.x + rect.w)
    const cy = Math.min(Math.max(y, rect.y), rect.y + rect.h)
    return { x: cx, y: cy }
}


